import { Box, Text } from '@factorialco/deprecated-design-system'
import { ProjectionKind, useSubprojectsDataTable } from 'generated/resources/datatable.graphql'
import compact from 'lodash/compact'

import i18n from 'modules/core/lib/i18n'

import { formatProjectHours } from 'modules/project_management/lib/formatHours'
import { ISubprojectsPanelData } from 'modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage/types'

import { Amount } from '../../Amount'
import ActionButtons from './ActionButtons'

type SubprojectsDataTableProjection = Parameters<typeof useSubprojectsDataTable>[0][number]

const SubprojectNameProjection = (): SubprojectsDataTableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'name',
  title: i18n.t('project_management.table.subprojects_list.headers.subproject'),
  render: ({ name }) => (
    <Box minWidth="s200">
      <Text.Normal overflowWrap>{name}</Text.Normal>
    </Box>
  ),
  template: '{{name}}',
  colSpan: 3,
  sortBy: 'name',
})

const SubprojectLaborCostProjection = ({
  currency,
}: {
  currency: string
}): SubprojectsDataTableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'laborCost',
  template: '{{ laborCostCents | divided_by: 100}}',
  title: i18n.t('project_management.table.subprojects_list.headers.labor_cost'),
  render: ({ laborCostCents }) => {
    return <Amount amount={laborCostCents} currency={currency} />
  },
})

const SubprojectHoursProjection = (): SubprojectsDataTableProjection => ({
  kind: ProjectionKind.Custom,
  identifier: 'hours',
  title: i18n.t('project_management.table.subprojects_list.headers.hours'),
  render: ({ inputedMinutes }) => (
    <Box minWidth="s80">
      <Text.Normal overflowWrap>{formatProjectHours(inputedMinutes)}</Text.Normal>
    </Box>
  ),
  template: 'hours',
  width: 200,
  colSpan: 3,
})

interface SubprojectActionsProjectionProps {
  showSubprojectsPanel: (newData: ISubprojectsPanelData) => void
  canManageSubprojects: boolean
}

const SubprojectActionsProjection = ({
  showSubprojectsPanel,
  canManageSubprojects,
}: SubprojectActionsProjectionProps): SubprojectsDataTableProjection => ({
  identifier: 'actions',
  kind: ProjectionKind.Decorative,
  render: ({ id, name, laborCostCents, inputedMinutes }) =>
    id && (
      <ActionButtons
        inputedMinutes={inputedMinutes}
        laborCostCents={laborCostCents}
        showSubprojectsPanel={showSubprojectsPanel}
        subprojectId={id}
        subprojectName={name}
        canManageSubprojects={canManageSubprojects}
      />
    ),
  width: 80,
})

export const subprojectsProjections = ({
  showSubprojectsPanel,
  canSeeCosts,
  currency,
  canManageSubprojects,
}: {
  showSubprojectsPanel: (newData: ISubprojectsPanelData) => void
  canSeeCosts: boolean
  currency: string
  canManageSubprojects: boolean
}): SubprojectsDataTableProjection[] =>
  compact([
    SubprojectNameProjection(),
    SubprojectHoursProjection(),
    canSeeCosts ? SubprojectLaborCostProjection({ currency }) : null,
    SubprojectActionsProjection({ showSubprojectsPanel, canManageSubprojects }),
  ])
